import { useState, useEffect } from 'react'
import Modal from './Modal'
import Boton from './Boton'
import MoneyInput from './MoneyInput'
import { notasVentaApi } from '../api/notasVenta'
import { pagosApi } from '../api/pagos'
import { fmtFecha, fmtMonto, parseMonto } from '../utils/fecha'

/**
 * Imputa un recibo YA creado (típicamente uno "libre"/a cuenta) a las NV del
 * cliente, con el mismo selector que la cobranza directa. Guarda el monto por
 * comprobante en el pivot (alimenta el saldo). No re-envía a Presea.
 *
 * Sirve igual para un contra-recibo de DEVOLUCIÓN (importe negativo): ahí los
 * montos van en negativo y lo que se puede aplicar a cada venta es lo que esa
 * venta tiene cobrado (Martin, 23 sep 2026 — recibo #774, que no se podía
 * imputar). El vendedor lo resuelve solo, sin tocar la base.
 *
 * pago: { id, importe, cliente:{id,...}, notas_venta:[{id, pivot:{monto}}] }
 */
export default function ModalImputarRecibo({ pago, onClose, onSaved }) {
  const [nvs, setNvs] = useState([])
  const [sel, setSel] = useState([])              // ids seleccionados
  const [montos, setMontos] = useState({})        // { [nvId]: 'monto' }
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  // Monto que ESTE recibo ya tiene imputado a cada NV (para poder re-disponerlo)
  const yaImputado = {}
  ;(pago.notas_venta || []).forEach(nv => { yaImputado[nv.id] = Number(nv.pivot?.monto) || 0 })

  useEffect(() => {
    if (!pago.cliente?.id) { setCargando(false); return }
    // Facturadas Y en borrador sin archivar (sugerencia #126, 27 oct 2026):
    // el cobro de una membresía extra que el motor del Wine Club dejó en una
    // NV duplicada tiene que poder ir a la venta del mes, que todavía no se
    // facturó. El listado ya excluye las archivadas (y trae las 30 más nuevas).
    notasVentaApi.listar({ cliente_id: pago.cliente.id, estados: 'procesado,borrador' })
      .then(({ data }) => {
        const items = data.data || []
        // Los pedidos a los que este recibo YA está vinculado entran siempre,
        // estén como estén (archivados incluidos): si se le cobró de más (el
        // envío de una experiencia, por ejemplo) hay que poder imputarle menos,
        // y sacarle la imputación a una NV duplicada para moverla a la buena.
        const ids = new Set(items.map(nv => nv.id))
        ;(pago.notas_venta || []).forEach(nv => { if (!ids.has(nv.id)) items.push(nv) })
        setNvs(items)
        // pre-cargar lo que este recibo ya tenía imputado
        const preSel = [], preMontos = {}
        items.forEach(nv => {
          if (yaImputado[nv.id]) { preSel.push(nv.id); preMontos[nv.id] = yaImputado[nv.id].toFixed(2) }
        })
        setSel(preSel); setMontos(preMontos)
      })
      .catch(() => setNvs([]))
      .finally(() => setCargando(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pago.cliente?.id])

  // Devolución = contra-recibo negativo: se aplica en negativo y lo que cada
  // venta puede absorber es lo que tiene COBRADO, no su saldo.
  const esDevolucion = (Number(pago.importe) || 0) < 0

  // Lo que se puede aplicar a una NV con ESTE recibo, siempre con el signo del
  // recibo: cobro → el saldo de la venta; devolución → lo que tiene cobrado.
  // En los dos casos se suma lo que este mismo recibo ya le tenía aplicado
  // (porque el saldo/cobrado ya lo cuenta).
  const dispoDe = (nv) => {
    const ya = yaImputado[nv.id] || 0
    return esDevolucion
      ? -((Number(nv.pagado) || 0) - ya)
      : (Number(nv.saldo ?? nv.total) || 0) + ya
  }
  const hayDispo = (nv) => Math.abs(dispoDe(nv)) > 0.005
  const nvsDispo = nvs.filter(hayDispo)

  const totalImputar = sel.reduce((acc, id) => acc + (parseMonto(montos[id]) || 0), 0)
  const libre = Math.round(((Number(pago.importe) || 0) - totalImputar) * 100) / 100
  // "Lo que falta aplicar" en valor absoluto y con el signo del recibo.
  const restanteRecibo = (excepto = null) => {
    const otros = sel
      .filter(id => id !== excepto)
      .reduce((acc, id) => acc + (parseMonto(montos[id]) || 0), 0)
    const resto = (Number(pago.importe) || 0) - otros
    return esDevolucion ? Math.min(0, resto) : Math.max(0, resto)
  }
  // El menor en valor absoluto, conservando el signo del recibo.
  const acotar = (a, b) => (esDevolucion ? Math.max(a, b) : Math.min(a, b))

  const toggle = (nv) => {
    setSel(prev => {
      if (prev.includes(nv.id)) {
        setMontos(m => { const n = { ...m }; delete n[nv.id]; return n })
        return prev.filter(x => x !== nv.id)
      }
      // arranca cubriendo lo máximo posible: lo menor (en valor absoluto)
      // entre lo que la venta admite y lo que al recibo le queda por aplicar
      const tope = acotar(dispoDe(nv), restanteRecibo(nv.id))
      setMontos(m => ({ ...m, [nv.id]: (Math.abs(tope) > 0.005 ? tope : dispoDe(nv)).toFixed(2) }))
      return [...prev, nv.id]
    })
  }

  // Completa el monto de este comprobante con el restante: lo máximo que puede
  // absorber (su saldo) limitado por lo que aún le queda al recibo por aplicar.
  const completar = (nv) => {
    const monto = acotar(dispoDe(nv), restanteRecibo(nv.id))
    setSel(prev => (prev.includes(nv.id) ? prev : [...prev, nv.id]))
    setMontos(m => ({ ...m, [nv.id]: monto.toFixed(2) }))
  }

  const guardar = async () => {
    const imputaciones = sel
      .map(id => ({ nv_id: id, monto: parseMonto(montos[id]) || 0 }))
      .filter(i => Math.abs(i.monto) > 0.005)
    if (imputaciones.length === 0) { setError('Cargá al menos un comprobante con monto.'); return }
    if (imputaciones.some(i => (i.monto < 0) !== esDevolucion)) {
      setError(esDevolucion
        ? 'Es una devolución: los montos van en negativo (lo que se le devolvió al cliente).'
        : 'Los montos van en positivo.')
      return
    }
    if (Math.abs(totalImputar) > Math.abs(Number(pago.importe) || 0) + 0.001) {
      setError('La imputación supera el importe del recibo.'); return
    }
    setGuardando(true); setError('')
    try {
      const { data } = await pagosApi.imputar(pago.id, imputaciones)
      onSaved?.(data)
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo imputar el recibo.')
    } finally { setGuardando(false) }
  }

  return (
    <Modal title={esDevolucion ? `Aplicar la devolución #${pago.id} a comprobantes` : `Imputar recibo #${pago.id} a comprobantes`} onClose={onClose} maxWidth="560px">
      <div style={st.resumen}>
        <div><span style={st.lbl}>{esDevolucion ? 'Importe devuelto' : 'Importe del recibo'}</span><strong>${fmtMonto(pago.importe)}</strong></div>
        <div>
          <span style={st.lbl}>Sin aplicar</span>
          <strong style={{ color: libre < -0.005 ? '#dc2626' : (libre < 0.005 ? '#16a34a' : '#1e40af') }}>${fmtMonto(libre)}</strong>
        </div>
      </div>

      {cargando ? (
        <p style={st.hint}>Cargando comprobantes…</p>
      ) : nvsDispo.length === 0 ? (
        <p style={st.hint}>{esDevolucion
          ? 'El cliente no tiene comprobantes con pagos aplicados: la devolución queda a cuenta.'
          : 'El cliente no tiene comprobantes con saldo para imputar.'}</p>
      ) : (
        <div style={st.lista}>
          <div style={st.listaTitle}>{esDevolucion ? 'Comprobantes del cliente con pagos aplicados' : 'Comprobantes del cliente con saldo'}</div>
          {nvsDispo.map(nv => {
            const dispo = dispoDe(nv)
            const isSel = sel.includes(nv.id)
            return (
              <div key={nv.id} style={st.row}>
                <label style={st.rowLabel}>
                  <input type="checkbox" checked={isSel} onChange={() => toggle(nv)} />
                  <span style={{ flex: 1 }}>
                    <strong>{nv.numero_presea || nv.numero_lgr}</strong>
                    {nv.estado === 'borrador' && <span style={st.tag}>{nv.archivado ? 'archivada' : 'borrador'}</span>}
                    <span style={st.fecha}>{fmtFecha(nv.fecha)}</span>
                  </span>
                  <span style={st.saldo}>${fmtMonto(dispo)}</span>
                </label>
                {isSel && esDevolucion && (
                  // Qué le pasa a la venta con lo que se está aplicando: una
                  // devolución imputada la deja DEBIENDO. Si la plata devuelta
                  // no era de esta venta, mejor dejarla a cuenta (Martin,
                  // 23 sep 2026 — LGR-20260907-YZQE1K).
                  <span style={st.efecto(((Number(nv.saldo) || 0) - (parseMonto(montos[nv.id]) || 0)) > 0.005)}>
                    {((Number(nv.saldo) || 0) - (parseMonto(montos[nv.id]) || 0)) > 0.005
                      ? `la venta queda debiendo $${fmtMonto((Number(nv.saldo) || 0) - (parseMonto(montos[nv.id]) || 0))}`
                      : 'la venta queda paga'}
                  </span>
                )}
                {isSel && (
                  <div style={st.montoWrap}>
                    <MoneyInput
                      style={st.montoInput}
                      value={montos[nv.id] ?? ''}
                      onChange={v => setMontos(m => ({ ...m, [nv.id]: v }))}
                      placeholder={dispo.toFixed(2)}
                      title="Monto a imputar a este comprobante"
                    />
                    <button
                      type="button"
                      style={st.restoBtn}
                      onClick={() => completar(nv)}
                      title="Completar con el restante del recibo"
                    >resto</button>
                  </div>
                )}
              </div>
            )
          })}
          <div style={st.totalRow}>
            <span>{esDevolucion ? 'Total a devolver' : 'Total a imputar'}</span>
            <strong>${fmtMonto(totalImputar)}</strong>
          </div>
        </div>
      )}

      {esDevolucion && (
        <p style={st.nota}>Lo que no apliques queda <strong>a cuenta del cliente</strong>. Si la plata devuelta no era de una venta en particular (por ejemplo, el sobrante de un pago web), dejala a cuenta: aplicarla a una venta la deja debiendo.</p>
      )}

      {error && <div style={st.error}>{error}</div>}

      <div style={st.acciones}>
        <Boton variante="ghost" onClick={onClose} type="button">Cancelar</Boton>
        <Boton variante="primary" onClick={guardar} disabled={guardando || cargando} type="button">
          {guardando ? 'Aplicando…' : (esDevolucion ? 'Aplicar devolución' : 'Imputar')}
        </Boton>
      </div>
    </Modal>
  )
}

const st = {
  resumen: { display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: '8px', marginBottom: '1rem' },
  lbl: { display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px' },
  hint: { color: '#94a3b8', fontSize: '0.875rem', padding: '0.5rem 0' },
  lista: { marginTop: '0.25rem' },
  listaTitle: { fontSize: '0.8125rem', fontWeight: '600', color: '#475569', marginBottom: '0.5rem' },
  row: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.5rem', borderRadius: '5px', background: '#f8fafc', marginBottom: '4px', fontSize: '0.8125rem' },
  rowLabel: { display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, cursor: 'pointer' },
  fecha: { marginLeft: '0.5rem', color: '#94a3b8', fontSize: '0.75rem' },
  tag: { marginLeft: '0.4rem', padding: '1px 6px', borderRadius: '4px', background: '#fef3c7', color: '#92400e', fontSize: '0.6875rem', fontWeight: 600 },
  saldo: { fontWeight: '700', color: '#1e40af', fontSize: '0.8125rem', marginLeft: 'auto' },
  montoWrap: { display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0 },
  montoInput: { width: '110px', padding: '0.375rem 0.5rem', border: '1px solid #93c5fd', borderRadius: '5px', fontSize: '0.8125rem', textAlign: 'right', boxSizing: 'border-box', flexShrink: 0 },
  restoBtn: { padding: '0.3rem 0.4rem', fontSize: '0.7rem', fontWeight: 700, color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '5px', cursor: 'pointer', flexShrink: 0 },
  totalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', fontSize: '0.8125rem', color: '#475569', borderTop: '1px solid #e2e8f0', marginTop: '0.375rem' },
  nota: { fontSize: '0.8rem', color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '0.5rem 0.7rem', margin: '0.75rem 0 0', lineHeight: 1.45 },
  efecto: (debe) => ({ fontSize: '0.72rem', fontWeight: 600, color: debe ? '#b45309' : '#166534', whiteSpace: 'nowrap', flexShrink: 0 }),
  error: { background: '#fee2e2', color: '#991b1b', padding: '0.625rem 0.875rem', borderRadius: '6px', margin: '0.75rem 0 0', fontSize: '0.8125rem' },
  acciones: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' },
}
